
import React, { useState, useEffect } from 'react';
import './Privacy.css';

export const Privacy = () => {
  const [activeSection, setActiveSection] = useState('');

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      window.history.pushState(null, '', `#${id}`);
      setActiveSection(id);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );

    const sections = document.querySelectorAll('section[id]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-header">
        <h1>Privacy Policy</h1>
        <p>Qinto Marketplace Platform</p>
        <p className="effective-date">Effective Date: November 23, 2025 | Last Updated: November 23, 2025</p>
      </div>

      <div className="legal-container">
        <aside className="legal-sidebar">
          <h3>Table of Contents</h3>
          <ul>
            <li><a href="#privacy-1" onClick={(e) => scrollToSection(e, 'privacy-1')} className={activeSection === 'privacy-1' ? 'active' : ''}>Introduction</a></li>
            <li><a href="#privacy-2" onClick={(e) => scrollToSection(e, 'privacy-2')} className={activeSection === 'privacy-2' ? 'active' : ''}>Information We Collect</a></li>
            <li><a href="#privacy-3" onClick={(e) => scrollToSection(e, 'privacy-3')} className={activeSection === 'privacy-3' ? 'active' : ''}>How We Use Your Information</a></li>
            <li><a href="#privacy-4" onClick={(e) => scrollToSection(e, 'privacy-4')} className={activeSection === 'privacy-4' ? 'active' : ''}>How We Share Your Information</a></li>
            <li><a href="#privacy-5" onClick={(e) => scrollToSection(e, 'privacy-5')} className={activeSection === 'privacy-5' ? 'active' : ''}>Data Retention</a></li>
            <li><a href="#privacy-6" onClick={(e) => scrollToSection(e, 'privacy-6')} className={activeSection === 'privacy-6' ? 'active' : ''}>Your Rights and Choices</a></li>
            <li><a href="#privacy-7" onClick={(e) => scrollToSection(e, 'privacy-7')} className={activeSection === 'privacy-7' ? 'active' : ''}>Security Measures</a></li>
            <li><a href="#privacy-8" onClick={(e) => scrollToSection(e, 'privacy-8')} className={activeSection === 'privacy-8' ? 'active' : ''}>Third-Party Services and Links</a></li>
            <li><a href="#privacy-9" onClick={(e) => scrollToSection(e, 'privacy-9')} className={activeSection === 'privacy-9' ? 'active' : ''}>Children's Privacy</a></li>
            <li><a href="#privacy-10" onClick={(e) => scrollToSection(e, 'privacy-10')} className={activeSection === 'privacy-10' ? 'active' : ''}>International Data Transfers</a></li>
            <li><a href="#privacy-11" onClick={(e) => scrollToSection(e, 'privacy-11')} className={activeSection === 'privacy-11' ? 'active' : ''}>Changes to This Privacy Policy</a></li>
            <li><a href="#privacy-12" onClick={(e) => scrollToSection(e, 'privacy-12')} className={activeSection === 'privacy-12' ? 'active' : ''}>Grievance Redressal</a></li>
            <li><a href="#privacy-13" onClick={(e) => scrollToSection(e, 'privacy-13')} className={activeSection === 'privacy-13' ? 'active' : ''}>Contact Us</a></li>
          </ul>
        </aside>

        <div className="legal-content">



          <section id="privacy-1">
            <h2>1. Introduction</h2>
            <p>Qinto ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Qinto mobile application and website at qinto.in (collectively, the "Platform").</p>
            <p>This Privacy Policy complies with the Information Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.</p>
            <p>By using the Platform, you consent to the collection, use, and disclosure of your information as described in this Privacy Policy.</p>
          </section>

          <section id="privacy-2">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide to Us</h3>
            <p><strong>Account Information:</strong></p>
            <ul>
              <li>Name or username</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Profile photo (optional)</li>
              <li>Location/city</li>
            </ul>

            <p><strong>Listing Information:</strong></p>
            <ul>
              <li>Product photos</li>
              <li>Product title, description, category</li>
              <li>Price and location</li>
              <li>Any other details you include in listings</li>
            </ul>

            <p><strong>Communication Data:</strong></p>
            <ul>
              <li>Messages sent through in-app chat</li>
              <li>Customer support inquiries</li>
              <li>Feedback and reviews</li>
            </ul>

            <p><strong>Transaction Information:</strong></p>
            <ul>
              <li>Details of items bought or sold (for record-keeping and dispute resolution)</li>
              <li>Communication with other users regarding transactions</li>
            </ul>

            <h3>2.2 Information We Collect Automatically</h3>
            <p><strong>Device Information:</strong></p>
            <ul>
              <li>Device type, model, and operating system</li>
              <li>Unique device identifiers</li>
              <li>Mobile network information</li>
              <li>IP address</li>
            </ul>

            <p><strong>Usage Information:</strong></p>
            <ul>
              <li>Pages or screens viewed</li>
              <li>Time spent on the Platform</li>
              <li>Search queries</li>
              <li>Features used</li>
              <li>Click and interaction data</li>
            </ul>

            <p><strong>Location Information:</strong></p>
            <ul>
              <li>Approximate location based on IP address</li>
              <li>Precise location (if you grant permission) for location-based features</li>
            </ul>

            <p><strong>Cookies and Similar Technologies:</strong></p>
            <ul>
              <li>We may use cookies, web beacons, and similar technologies to collect information about your browsing activities and preferences</li>
            </ul>

            <h3>2.3 Information from Third Parties</h3>
            <p><strong>Social Media:</strong></p>
            <ul>
              <li>If you choose to sign in using Google, Facebook, or other third-party services, we may receive information from those services, such as your name, email address, and profile photo</li>
            </ul>

            <p><strong>Analytics Providers:</strong></p>
            <ul>
              <li>We may receive aggregated or de-identified data from third-party analytics services</li>
            </ul>
          </section>

          <section id="privacy-3">
            <h2>3. How We Use Your Information</h2>
            <p>We use your information for the following purposes:</p>

            <h3>3.1 Provide and Improve the Platform</h3>
            <ul>
              <li>Create and manage your account</li>
              <li>Enable you to create, view, and manage listings</li>
              <li>Facilitate communication between buyers and sellers</li>
              <li>Process and complete transactions (when payment features are available)</li>
              <li>Provide customer support</li>
              <li>Send service-related notifications and updates</li>
              <li>Improve Platform functionality and user experience</li>
              <li>Develop new features and services</li>
            </ul>

            <h3>3.2 Safety and Security</h3>
            <ul>
              <li>Verify your identity</li>
              <li>Detect and prevent fraud, abuse, and illegal activity</li>
              <li>Enforce our Terms and Conditions</li>
              <li>Resolve disputes and troubleshoot problems</li>
              <li>Protect the rights, property, and safety of Qinto, users, and the public</li>
            </ul>

            <h3>3.3 Communication</h3>
            <ul>
              <li>Respond to your inquiries and requests</li>
              <li>Send administrative messages and notifications</li>
              <li>Send promotional emails and notifications about new features (with your consent)</li>
              <li>Conduct surveys and collect feedback</li>
            </ul>

            <h3>3.4 Analytics and Research</h3>
            <ul>
              <li>Analyze usage patterns and trends</li>
              <li>Conduct market research</li>
              <li>Generate aggregated, de-identified statistics</li>
            </ul>

            <h3>3.5 Legal Compliance</h3>
            <ul>
              <li>Comply with applicable laws, regulations, and legal processes</li>
              <li>Respond to requests from law enforcement or government agencies</li>
            </ul>
          </section>

          <section id="privacy-4">
            <h2>4. How We Share Your Information</h2>
            <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

            <h3>4.1 With Other Users</h3>
            <ul>
              <li>Your profile information (name, photo, location) is visible to other users</li>
              <li>Your listings are visible to all Platform users</li>
              <li>Messages you send are visible to the recipients</li>
              <li>Ratings and reviews (if/when implemented) may be visible to other users</li>
            </ul>

            <h3>4.2 With Service Providers</h3>
            <p>We may share your information with third-party service providers who perform services on our behalf, including:</p>
            <ul>
              <li>Cloud hosting and storage providers (e.g., Firebase, Google Cloud)</li>
              <li>Analytics providers (e.g., Google Analytics, Firebase Analytics)</li>
              <li>Customer support tools</li>
              <li>Email and communication services</li>
              <li>Payment processors (when payment features are available)</li>
            </ul>
            <p>These service providers are contractually obligated to protect your information and use it only for the purposes for which it was disclosed.</p>

            <h3>4.3 For Legal Reasons</h3>
            <p>We may disclose your information if required by law or in good faith belief that such disclosure is necessary to:</p>
            <ul>
              <li>Comply with legal obligations, court orders, or government requests</li>
              <li>Enforce our Terms and Conditions</li>
              <li>Protect the rights, property, or safety of Qinto, users, or the public</li>
              <li>Detect, prevent, or address fraud, security, or technical issues</li>
            </ul>

            <h3>4.4 Business Transfers</h3>
            <p>If Qinto is involved in a merger, acquisition, sale of assets, or bankruptcy, your information may be transferred as part of that transaction. We will notify you via email and/or prominent notice on the Platform of any change in ownership or use of your personal information.</p>

            <h3>4.5 With Your Consent</h3>
            <p>We may share your information with third parties when you have given explicit consent to do so.</p>
          </section>

          <section id="privacy-5">
            <h2>5. Data Retention</h2>
            <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>
            <ul>
              <li><strong>Account information:</strong> Retained until you delete your account</li>
              <li><strong>Listing information:</strong> Retained until you delete the listing or your account</li>
              <li><strong>Communication data:</strong> Retained for a reasonable period for dispute resolution</li>
              <li><strong>Usage data:</strong> Retained for analytics purposes, typically in aggregated form</li>
            </ul>
            <p>After account deletion, we may retain certain information in de-identified form for analytics and legal compliance purposes.</p>
          </section>

          <section id="privacy-6">
            <h2>6. Your Rights and Choices</h2>
            <p>You have the following rights regarding your personal information:</p>

            <h3>6.1 Access and Update</h3>
            <p>You can access and update your account information at any time through Profile → Settings in the app.</p>

            <h3>6.2 Delete Account</h3>
            <p>You may delete your account at any time through Profile → Settings → Account → Delete Account. Upon deletion, we will remove your personal information, except as required for legal compliance or legitimate business purposes.</p>

            <h3>6.3 Opt-Out of Marketing Communications</h3>
            <p>You can opt out of promotional emails by:</p>
            <ul>
              <li>Clicking the "unsubscribe" link in any promotional email</li>
              <li>Updating your notification preferences in the app settings</li>
            </ul>
            <p>You cannot opt out of service-related communications (e.g., account verification, transaction confirmations).</p>

            <h3>6.4 Location Permissions</h3>
            <p>You can control location permissions through your device settings. Disabling location services may limit certain Platform features.</p>

            <h3>6.5 Cookie Management</h3>
            <p>You can manage cookie preferences through your browser settings. Note that disabling cookies may affect Platform functionality.</p>

            <h3>6.6 Data Portability</h3>
            <p>You may request a copy of your personal information in a structured, machine-readable format by contacting us at support@qinto.in.</p>

            <h3>6.7 Withdraw Consent</h3>
            <p>Where we rely on your consent to process your information, you may withdraw consent at any time by contacting us.</p>
          </section>

          <section id="privacy-7">
            <h2>7. Security Measures</h2>
            <p>We take the security of your personal information seriously and implement reasonable security practices and procedures, including:</p>
            <ul>
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Encryption of sensitive data at rest</li>
              <li>Secure authentication mechanisms</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication</li>
              <li>Firewall protection</li>
            </ul>
            <div className="warning-box">
              <p>However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>
            </div>
            <p><strong>You are responsible for:</strong></p>
            <ul>
              <li>Maintaining the confidentiality of your password</li>
              <li>Restricting access to your device</li>
              <li>Logging out after using the Platform on shared devices</li>
            </ul>
          </section>

          <section id="privacy-8">
            <h2>8. Third-Party Services and Links</h2>
            <p>The Platform may contain links to third-party websites, services, or advertisements. We are not responsible for the privacy practices or content of these third parties. We encourage you to review the privacy policies of any third-party services before providing your information.</p>

            <h3>8.1 Third-Party Services We Use</h3>
            <ul>
              <li><strong>Firebase (Google):</strong> Authentication, database, storage, and analytics</li>
              <li><strong>Google Analytics:</strong> Usage analytics</li>
              <li><strong>Google Sign-In:</strong> Optional authentication method</li>
              <li><strong>Payment processors (future):</strong> When payment features are implemented</li>
            </ul>
            <p>These services have their own privacy policies and data practices.</p>
          </section>

          <section id="privacy-9">
            <h2>9. Children's Privacy</h2>
            <p>The Platform is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected information from a child under 18, we will take steps to delete such information promptly.</p>
            <p>If you are a parent or guardian and believe your child has provided us with personal information, please contact us at support@qinto.in.</p>
          </section>

          <section id="privacy-10">
            <h2>10. International Data Transfers</h2>
            <p>Your information may be transferred to and processed in countries other than your country of residence, including India and other countries where our service providers operate. These countries may have data protection laws that are different from the laws of your country.</p>
            <p>By using the Platform, you consent to the transfer of your information to these countries. We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.</p>
          </section>

          <section id="privacy-11">
            <h2>11. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by:</p>
            <ul>
              <li>Updating the "Last Updated" date at the top of this Privacy Policy</li>
              <li>Posting a notice on the Platform</li>
              <li>Sending an email notification to your registered email address</li>
              <li>In-app notification</li>
            </ul>
            <p>Your continued use of the Platform after the changes take effect constitutes acceptance of the updated Privacy Policy. We encourage you to review this Privacy Policy periodically.</p>
          </section>

          <section id="privacy-12">
            <h2>12. Grievance Redressal</h2>
            <p>In accordance with the Information Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, we have appointed a Grievance Officer to address privacy concerns.</p>
            <div className="info-box">
              <p><strong>Grievance Officer:</strong><br />
                Name: Deepak J<br />
                Email: support@qinto.in<br />
                Response Time: We will acknowledge your complaint within 24 hours and resolve it within 30 days.</p>
            </div>
          </section>

          <section id="privacy-13">
            <h2>13. Contact Us</h2>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
            <div className="info-box">
              <p><strong>Qinto Privacy Team</strong><br />
                Email: support@qinto.in<br />
                Website: <a href="https://qinto.in" target="_blank" rel="noopener noreferrer">https://www.qinto.in</a></p>
              <p>We will respond to your inquiry within 7 business days.</p>
            </div>

            <div className="warning-box" style={{ marginTop: '2rem' }}>
              <p><strong>Consent:</strong></p>
              <p>By using the Qinto Platform, you acknowledge that you have read, understood, and agree to the collection, use, and disclosure of your information as described in this Privacy Policy.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;